import { Injectable, NgZone, effect, inject, signal } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { Messaging, getToken, onMessage } from '@angular/fire/messaging';
import type { MessagePayload } from 'firebase/messaging';
import { firstValueFrom } from 'rxjs';
import { ApiClientsService } from '../api/api-clients.service';
import { NotificationConfigDto } from '../api/models/notification.models';
import { FirebaseAuthService } from '../auth/firebase-auth.service';
import { FamilyService } from '../families/family.service';

@Injectable({ providedIn: 'root' })
export class PushNotificationsService {
  private readonly messaging = inject(Messaging, { optional: true });
  private readonly snackBar = inject(MatSnackBar);
  private readonly api = inject(ApiClientsService);
  private readonly auth = inject(FirebaseAuthService);
  private readonly families = inject(FamilyService);
  private readonly zone = inject(NgZone);
  private readonly router = inject(Router);

  private readonly config = signal<NotificationConfigDto | null>(null);
  private readonly prompting = signal(false);
  private readonly latestToken = signal<string | null>(null);
  private configLoaded = false;
  private registerPromise: Promise<void> | null = null;

  constructor() {
    effect(() => {
      const user = this.auth.user();
      if (!user) {
        this.config.set(null);
        this.configLoaded = false;
        this.latestToken.set(null);
        return;
      }

      if (!this.configLoaded) {
        void this.loadConfig();
      }
    });

    effect(
      () => {
        const config = this.config();
        if (!config?.useFirebaseMessaging) {
          return;
        }

        const user = this.auth.user();
        const family = this.families.selectedFamily();
        if (!user || !family) {
          return;
        }

        const permission = this.getNotificationPermission();
        if (permission === 'granted') {
          void this.registerDevice();
          return;
        }

        if (permission === 'default') {
          this.promptForPermission(user.uid);
        }
      },
      { allowSignalWrites: true }
    );

    if (this.messaging) {
      onMessage(this.messaging, (payload) => {
        this.zone.run(() => this.handleForegroundMessage(payload));
      });
    }

    this.listenForServiceWorkerMessages();
  }

  private async loadConfig() {
    this.configLoaded = true;
    try {
      const config = await firstValueFrom(this.api.getNotificationConfig());
      this.config.set(config);
    } catch (error) {
      console.warn('Failed to load notification config', error);
      this.config.set(null);
    }
  }

  private getNotificationPermission(): NotificationPermission | 'unsupported' {
    if (typeof Notification === 'undefined') {
      return 'unsupported';
    }
    return Notification.permission;
  }

  private promptForPermission(userId: string) {
    if (!this.canUseNotifications() || !userId || this.prompting()) {
      return;
    }

    if (this.hasPrompted(userId)) {
      return;
    }

    this.markPrompted(userId);
    this.prompting.set(true);
    const ref = this.snackBar.open('Turn on appreciation alerts?', 'Enable', {
      duration: 8000
    });
    ref.afterDismissed().subscribe(() => this.prompting.set(false));
    ref.onAction().subscribe(() => {
      void this.requestPermissionAndRegister();
    });
  }

  private async requestPermissionAndRegister() {
    if (!this.canUseNotifications() || typeof Notification === 'undefined') {
      this.snackBar.open('Push notifications are not supported in this browser yet.', undefined, {
        duration: 4000
      });
      return;
    }

    try {
      const result = await Notification.requestPermission();
      if (result !== 'granted') {
        this.snackBar.open('No worries—you can enable alerts later from browser settings.', undefined, {
          duration: 5000
        });
        return;
      }
      await this.registerDevice();
    } catch (error) {
      console.error('Unable to request notification permission', error);
      this.snackBar.open('We could not enable notifications right now. Try again later.', undefined, {
        duration: 5000
      });
    }
  }

  private async registerDevice() {
    if (!this.messaging || !this.canUseNotifications()) {
      return;
    }

    if (this.registerPromise) {
      return this.registerPromise;
    }

    this.registerPromise = this.performRegistration().finally(() => {
      this.registerPromise = null;
    });

    return this.registerPromise;
  }

  private async performRegistration() {
    const config = this.config();
    if (!config?.useFirebaseMessaging) {
      return;
    }

    const registration = await this.ensureMessagingServiceWorker();
    if (!registration) {
      this.snackBar.open('Push notifications need a supported service worker.', undefined, {
        duration: 5000
      });
      return;
    }

    try {
      const token = await getToken(this.messaging!, {
        vapidKey: config.vapidPublicKey ?? undefined,
        serviceWorkerRegistration: registration
      });

      if (!token) {
        this.snackBar.open('We could not register this device for alerts.', undefined, {
          duration: 5000
        });
        return;
      }

      if (this.latestToken() === token) {
        return;
      }

      await firstValueFrom(
        this.api.registerPushSubscription({
          token,
          platform: 'web',
          deviceName: this.buildDeviceName()
        })
      );
      this.latestToken.set(token);
      this.snackBar.open('Appreciation alerts are on for this device.', undefined, {
        duration: 4000
      });
    } catch (error) {
      console.error('Failed to register push subscription', error);
      this.snackBar.open('We hit a snag setting up alerts. Try again later.', undefined, {
        duration: 5000
      });
    }
  }

  private async ensureMessagingServiceWorker(): Promise<ServiceWorkerRegistration | null> {
    if (!this.isBrowserSupported() || !('serviceWorker' in navigator)) {
      return null;
    }

    try {
      const existing = await navigator.serviceWorker.getRegistration('/firebase-messaging-sw.js');
      if (existing) {
        return existing;
      }

      return await navigator.serviceWorker.register('/firebase-messaging-sw.js', {
        scope: '/firebase-cloud-messaging-push-scope'
      });
    } catch (error) {
      console.warn('Failed to register firebase messaging service worker', error);
      return null;
    }
  }

  private hasPrompted(userId: string) {
    if (!this.isBrowserSupported()) {
      return false;
    }
    return localStorage.getItem(this.promptStorageKey(userId)) === '1';
  }

  private markPrompted(userId: string) {
    if (!this.isBrowserSupported()) {
      return;
    }
    localStorage.setItem(this.promptStorageKey(userId), '1');
  }

  private promptStorageKey(userId: string) {
    return `fruitage.pushPrompted.v1.${userId}`;
  }

  private canUseNotifications() {
    return this.isBrowserSupported() && 'Notification' in window && window.isSecureContext;
  }

  private isBrowserSupported() {
    return typeof window !== 'undefined' && typeof localStorage !== 'undefined';
  }

  private buildDeviceName() {
    if (!this.isBrowserSupported()) {
      return 'web';
    }

    const nav = window.navigator as Navigator & {
      userAgentData?: { platform?: string; brands?: { brand: string }[] };
      userAgent: string;
    };
    const platform = nav.userAgentData?.platform ?? nav.platform ?? 'web';
    const browser = this.detectBrowser(nav.userAgentData?.brands, nav.userAgent);
    return browser ? `${platform} – ${browser}` : platform;
  }

  private detectBrowser(brands: { brand: string }[] | undefined, fallback: string) {
    if (brands?.length) {
      const candidate = brands.find((brand) => brand.brand && brand.brand !== 'Not:A-Brand');
      if (candidate?.brand) {
        return candidate.brand;
      }
    }

    if (fallback?.toLowerCase().includes('safari')) {
      return 'Safari';
    }
    if (fallback?.toLowerCase().includes('firefox')) {
      return 'Firefox';
    }
    if (fallback?.toLowerCase().includes('edg')) {
      return 'Edge';
    }
    if (fallback?.toLowerCase().includes('chrome')) {
      return 'Chrome';
    }
    return '';
  }

  private handleForegroundMessage(payload: MessagePayload) {
    const title = payload.notification?.title ?? 'New appreciation';
    const body = payload.notification?.body ?? 'Open Fruitage to read it.';
    const target = payload.data?.['path'] ?? payload.data?.['link'];

    if (target) {
      const ref = this.snackBar.open(`${title}: ${body}`, 'View', { duration: 6000 });
      ref.onAction().subscribe(() => this.navigateTo(target));
      return;
    }

    this.snackBar.open(`${title}: ${body}`, undefined, { duration: 6000 });
  }

  private listenForServiceWorkerMessages() {
    if (!this.isBrowserSupported() || !('serviceWorker' in navigator)) {
      return;
    }

    navigator.serviceWorker.addEventListener('message', (event) => {
      const data = event.data as { type?: string; url?: string } | undefined;
      if (data?.type === 'navigate' && data.url) {
        this.zone.run(() => this.navigateTo(data.url!));
      }
    });
  }

  private navigateTo(url: string) {
    if (!this.isBrowserSupported()) {
      return;
    }

    try {
      const target = new URL(url, window.location.origin);
      void this.router.navigateByUrl(target.pathname + target.search + target.hash);
    } catch {
      void this.router.navigateByUrl('/thankful');
    }
  }
}
